import { useRef, useState } from 'react';
import { useConfirm } from '@/features/common/ConfirmDialog/useConfirm';
import { communityApi } from '@/features/publicsite/community/api/communityApi';
import { publicCommunity } from '@/i18n/dictionaries/publicCommunity';
import { useTranslation } from '@/i18n/useTranslation';
import { detailOr } from '@/services/api';

/**
 * "Invită": the owner's window with the space's access code. The code is read when the window
 * opens, not with the page — it is the owner's alone, and a member's page load would only be
 * refused (403). Replacing it is asked first, since the old code stops letting anyone in.
 * A refusal stays in the window; `notice` is what the window says after a copy or a renewal.
 */
export function useInviteDialog(forumId: string) {
  const { t } = useTranslation(publicCommunity);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const show = async () => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setCode(null);
    setRefusal(null);
    setNotice(null);
    setOpen(true);
    setBusy(true);
    try {
      setCode((await communityApi.accessCode(forumId)).code);
    } catch (e) {
      setRefusal(detailOr(e, t('codeError')));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (!busy) setOpen(false);
  };

  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setNotice(t('codeCopied'));
    } catch {
      setNotice(t('copyFailed'));
    }
  };

  const renew = async () => {
    const confirmed = await confirm({
      title: t('renewConfirmTitle'),
      body: t('renewConfirmBody'),
      confirmLabel: t('renewCode'),
      tone: 'danger',
    });
    if (!confirmed) return;
    setBusy(true);
    setRefusal(null);
    setNotice(null);
    try {
      setCode((await communityApi.renewAccessCode(forumId)).code);
      setNotice(t('codeRenewed'));
    } catch (e) {
      setRefusal(detailOr(e, t('renewError')));
    } finally {
      setBusy(false);
    }
  };

  return {
    open,
    code,
    busy,
    refusal,
    notice,
    opener,
    show,
    close,
    copy,
    renew,
    onClosed: () => setOpen(false),
    confirmDialog,
  };
}
